import useSWR from "swr";
import { useToken } from "../auth/token";
import { getBookmarks, addBookmark, removeBookmark, updateBookmarkOrder } from "../../api/bookmark";

const BOOKMARK_KEY = "/bookmark";

export function useFavourite() {
  const { token } = useToken();
  
  const { data, mutate, error, isValidating } = useSWR(
    token ? BOOKMARK_KEY : null,
    async () => {
      if (!token) {
        return [];
      }
      
      try {
        const response = await getBookmarks(token);
        if (response?.code === 0) {
          return response?.result || [];
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
    }
  );

  const addFavourite = async (item: any) => {
    const itemId = item?.ep_id || item?.id; // ep_id가 없으면 id 사용
    
    if (!token || !itemId) {
      return;
    }

    try {
      const currentData = data || [];
      
      // item 객체에 ep_id가 없으면 추가해주기
      const itemToAdd = {
        ...item,
        ep_id: item?.ep_id || item?.id
      };
      
      const updatedData = [...currentData, itemToAdd];
      
      mutate(updatedData, false);

      // API 호출
      const response = await addBookmark(token, itemId);
      
      if (response?.code === 0) {
        mutate();
      } else {
        // 실패 시 이전 상태로 롤백
        mutate(currentData, false);
      }
    } catch (error) {
      // 에러 시 이전 상태로 롤백
      const currentData = data || [];
      mutate(currentData, false);
    }
  };

  const removeFavourite = async (item: any) => {
    if (!token) {
      return;
    }
    
    const itemId = item?.ep_id || item?.id; // ep_id가 없으면 id 사용
    
    if (!itemId) {
      return;
    }

    try {
      // 업데이트 (UI 즉시 반영)
      const currentData = data || [];
      
      const updatedData = currentData.filter((fav: any) => {
        const favId = fav?.ep_id || fav?.id;
        return favId !== itemId;
      });
      
      mutate(updatedData, false);

      // API 호출
      const response = await removeBookmark(token, itemId);
      
      if (response?.code === 0) {
        // 성공적으로 삭제됨
        mutate();
      } else {
        // 실패 시 이전 상태로 롤백
        mutate(currentData, false);
      }
    } catch (error) {
      // 에러 시 이전 상태로 롤백
      const currentData = data || [];
      mutate(currentData, false);
    }
  };

  const toggleFavourite = async (item: any) => {
    
    if (!data) {
      return;
    }
    
    const itemId = item?.ep_id || item?.id; // ep_id가 없으면 id 사용
    
    const isBookmarked = data.find((fav: any) => {
      const favId = fav?.ep_id || fav?.id;
      return favId === itemId;
    });
    
    if (isBookmarked) {
      await removeFavourite(item);
    } else {
      await addFavourite(item);
    }
  };

  const updateFavouriteOrder = async (reorderedBookmarks: any[]) => {
    if (!token || !reorderedBookmarks.length) {
      return;
    }

    try {
      // UI 즉시 반영 (낙관적 업데이트)
      mutate(reorderedBookmarks, false);

      // API 형식에 맞게 데이터 변환
      const bookmarkOrder = reorderedBookmarks.map((bookmark, index) => ({
        id: bookmark.id,
        sort_order: index + 1
      }));

      // API 호출
      const response = await updateBookmarkOrder(token, bookmarkOrder);
      
      if (response?.success) {
        // 성공 시 최신 데이터로 갱신
        mutate();
      } else {
        // 실패 시 원래 데이터로 롤백
        mutate();
      }
    } catch (error) {
      console.error('❌ 북마크 순서 변경 에러:', error);
      // 에러 시 원래 데이터로 롤백
      mutate();
    }
  };

  return {
    data: data || [],
    isLoading: isValidating,
    isError: error,
    addFavourite,
    removeFavourite,
    toggleFavourite,
    updateFavouriteOrder,
    mutate, // 수동으로 새로고침할 때 사용
  };
}
